import { useState } from 'react';
import { BundleStore, useBundleStore } from '../hooks/bundle-store';
import * as THREE from 'three';
import { computeBoundingBox } from '../3d/camera';
import { useShallow } from 'zustand/react/shallow';

export default function ResetButton({
  cameraControlRef,
  modelsRef,
}: {
  cameraControlRef: any;
  modelsRef: THREE.Group | null;
}) {
  const [isActive, setIsActive] = useState(false);

  const { enableZoom, isSingle, isApparel } = useBundleStore(
    useShallow((state: BundleStore) => ({
      isSingle: state?.bundle?.isSingle,
      enableZoom: state.enableZoom,
      isApparel: state.bundle?.isApparel,
    })),
  );

  const handleReset3dView = () => {
    cameraControlRef.rotatePolarTo(Math.PI / 2.5, true);
    cameraControlRef.rotateAzimuthTo(Math.PI / 3, true);
    cameraControlRef.fitToSphere(modelsRef, true).then(() => {
      if (!isSingle && !isApparel) {
        cameraControlRef.fitToSphere(modelsRef, true);

        const boundingBox = computeBoundingBox(modelsRef as THREE.Group);
        const height = boundingBox.max.y - boundingBox.min.y;
        const offsetY = (height * 10) / 100;

        const target = new THREE.Vector3();
        cameraControlRef.getTarget(target);

        target.y -= offsetY;
        cameraControlRef.moveTo(target.x, target.y, target.z, true);
        cameraControlRef.dolly(enableZoom ? 1.3 : 0.6, true);
      }
    });
  };

  return (
    !enableZoom && (
      <div
        style={{
          position: 'absolute',
          bottom: 10,
          left: 10,
        }}>
        <div
          style={{
            width: '32px',
            height: '32px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'white',
            borderRadius: '50%',
            border: '2px solid white',
            color: 'black',
            cursor: 'pointer',
            fontSize: '12px',
            padding: '4px',
            boxShadow: isActive ? '0 0 5px rgba(0, 0, 0, 0.15)' : 'none',
            transition: 'all 0.2s ease-in-out',
          }}
          onMouseDown={() => setIsActive(true)}
          onMouseUp={() => setIsActive(false)}
          onMouseLeave={() => setIsActive(false)}
          onClick={handleReset3dView}
          title="Reset 3D">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5.66669 0.666672H1.50002C1.27901 0.666672 1.06704 0.754469 0.910765 0.910749C0.754484 1.06703 0.666687 1.27899 0.666687 1.50001V5.66667C0.666687 5.88769 0.754484 6.09965 0.910765 6.25593C1.06704 6.41221 1.27901 6.50001 1.50002 6.50001C1.72103 6.50001 1.933 6.41221 2.08928 6.25593C2.24556 6.09965 2.33335 5.88769 2.33335 5.66667V2.33334H5.66669C5.8877 2.33334 6.09966 2.24554 6.25594 2.08926C6.41222 1.93298 6.50002 1.72102 6.50002 1.50001C6.50002 1.27899 6.41222 1.06703 6.25594 0.910749C6.09966 0.754469 5.8877 0.666672 5.66669 0.666672ZM5.66669 15.6667H2.33335V12.3333C2.33335 12.1123 2.24556 11.9004 2.08928 11.7441C1.933 11.5878 1.72103 11.5 1.50002 11.5C1.27901 11.5 1.06704 11.5878 0.910765 11.7441C0.754484 11.9004 0.666687 12.1123 0.666687 12.3333V16.5C0.666687 16.721 0.754484 16.933 0.910765 17.0893C1.06704 17.2455 1.27901 17.3333 1.50002 17.3333H5.66669C5.8877 17.3333 6.09966 17.2455 6.25594 17.0893C6.41222 16.933 6.50002 16.721 6.50002 16.5C6.50002 16.279 6.41222 16.067 6.25594 15.9107C6.09966 15.7545 5.8877 15.6667 5.66669 15.6667ZM16.5 0.666672H12.3334C12.1123 0.666672 11.9004 0.754469 11.7441 0.910749C11.5878 1.06703 11.5 1.27899 11.5 1.50001C11.5 1.72102 11.5878 1.93298 11.7441 2.08926C11.9004 2.24554 12.1123 2.33334 12.3334 2.33334H15.6667V5.66667C15.6667 5.88769 15.7545 6.09965 15.9108 6.25593C16.067 6.41221 16.279 6.50001 16.5 6.50001C16.721 6.50001 16.933 6.41221 17.0893 6.25593C17.2456 6.09965 17.3334 5.88769 17.3334 5.66667V1.50001C17.3334 1.27899 17.2456 1.06703 17.0893 0.910749C16.933 0.754469 16.721 0.666672 16.5 0.666672ZM16.5 11.5C16.279 11.5 16.067 11.5878 15.9108 11.7441C15.7545 11.9004 15.6667 12.1123 15.6667 12.3333V15.6667H12.3334C12.1123 15.6667 11.9004 15.7545 11.7441 15.9107C11.5878 16.067 11.5 16.279 11.5 16.5C11.5 16.721 11.5878 16.933 11.7441 17.0893C11.9004 17.2455 12.1123 17.3333 12.3334 17.3333H16.5C16.721 17.3333 16.933 17.2455 17.0893 17.0893C17.2456 16.933 17.3334 16.721 17.3334 16.5V12.3333C17.3334 12.1123 17.2456 11.9004 17.0893 11.7441C16.933 11.5878 16.721 11.5 16.5 11.5Z"
              fill="var(--primary-color)"
            />
          </svg>
        </div>
      </div>
    )
  );
}
